"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import EnquiryModal from "@/components/EnquiryModal";
import { AvailabilityBars } from "@/components/v2/AvailabilityBars";
import { Button } from "@/components/v2/Button";
import { FloorPlanCard } from "@/components/v2/FloorPlanCard";
import { Pill } from "@/components/v2/Pill";
import { StickyEnquiryBar } from "@/components/v2/StickyEnquiryPanel";
import { UnlockModal } from "@/components/v2/UnlockModal";
import { floorPlanImageFor, summarise, Unit } from "@/lib/v2/availability";
import { formatPriceShort, formatPsf, statusMeta, tenureLabel } from "@/lib/v2/format";
import { useUnlock } from "@/lib/v2/unlock";
import { ScrollRow } from "@/components/v2/ScrollRow";
import { Lock } from "lucide-react";
import { formatProjectName } from "@/lib/utils";
import { PriceListTable, PriceListUnit } from "./PriceListTable";

export type PriceListProperty = {
  _id?: string;
  name?: string;
  title?: string;
  district?: string;
  construction_status?: string;
  isPreLaunch?: boolean;
  is_leasehold?: boolean;
  lease_year?: number | null;
  updatedAt?: string;
  images?: { floor_images?: string[] };
};

function formattedDate(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Singapore",
  });
}

function toSummaryUnits(units: PriceListUnit[]): Unit[] {
  return units.map((unit) => ({
    flat_id: unit.id,
    unit: unit.unit,
    price: unit.price,
    area: unit.area,
    status: unit.status,
    price_psf: unit.pricePerSf,
    plan: unit.plan,
    type: unit.type,
    type_feature: unit.typeFeature ?? null,
  }));
}

function displayType(unit: PriceListUnit): string {
  return unit.typeFeature ? `${unit.type} + ${unit.typeFeature}` : unit.type;
}

export function PriceListExperience({
  routeId,
  property,
  units,
  loadError,
}: {
  routeId: string;
  property?: PriceListProperty;
  units: PriceListUnit[];
  loadError?: boolean;
}) {
  const propertyId = property?._id || routeId;
  const slugFromHref = String(routeId ?? "");
  const propertyName =
    property?.name ||
    property?.title ||
    slugFromHref.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
    "This project";
  const projectHref = `/single-property/${routeId}`;
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const { unlocked } = useUnlock(propertyId);

  const summary = useMemo(() => summarise(toSummaryUnits(units)), [units]);
  const availableUnits = useMemo(
    () => units.filter((unit) => unit.status.trim().toLowerCase() === "available"),
    [units],
  );
  const availablePrices = availableUnits
    .map((unit) => unit.price)
    .filter((price): price is number => price != null && price > 0);
  const availablePsfs = availableUnits
    .map((unit) => unit.pricePerSf)
    .filter((psf): psf is number => psf != null && psf > 0);
  const priceFrom = availablePrices.length ? Math.min(...availablePrices) : null;
  const psfFrom = availablePsfs.length ? Math.min(...availablePsfs) : null;
  const psfTo = availablePsfs.length ? Math.max(...availablePsfs) : null;
  const updatedAt = formattedDate(property?.updatedAt);
  const projectStatus = property?.construction_status || "Upcoming";
  const status = statusMeta(projectStatus, property?.isPreLaunch);
  const soldOut = projectStatus.trim().toLowerCase() === "sold out" || (summary.total > 0 && summary.available === 0);
  const floorImages = property?.images?.floor_images ?? [];
  const imageBase = process.env.NEXT_PUBLIC_IMAGE_URL;
  const floorPlanImage = (plan: string) => {
    const file = floorPlanImageFor(plan, floorImages);
    if (!file || !imageBase || !propertyName) return undefined;
    return `${imageBase}/${formatProjectName(propertyName)}/floor_images/${file}`;
  };

  const choosePlan = (plan: string) => {
    setSelectedPlan(plan);
    const table = document.getElementById("table");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    table?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  return (
    <main className="pb-24 md:pb-12">
      <div className="mx-auto max-w-site px-4 py-8 md:px-8 md:py-12">
        {soldOut && (
          <div className="mb-6 rounded-2xl border border-sold/20 bg-sold-soft px-5 py-4 text-sold" role="status">
            <p className="font-display font-semibold">This project is fully sold.</p>
            <p className="mt-1 text-sm">The table below is the final record.</p>
          </div>
        )}

        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-ink-faint">
          <Link href={projectHref} className="rounded hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">{propertyName}</Link>
          <ChevronRight aria-hidden="true" className="size-4" />
          <span aria-current="page">Price list</span>
        </nav>

        <header className="mt-5 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="font-display text-4xl font-semibold tracking-tight text-ink md:text-5xl">{propertyName} price list</h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-ink-muted">
              Every unit, its status, size and PSF.{updatedAt ? ` Updated ${updatedAt}.` : ""}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Pill tone={status.tone}>{status.label}</Pill>
              {property?.district && <Pill tone="neutral">{property.district}</Pill>}
              <Pill tone="neutral">{tenureLabel(property?.is_leasehold ?? false, property?.lease_year)}</Pill>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            {!unlocked && !soldOut && summary.total > 0 && (
              <Button onClick={() => setUnlockOpen(true)} className="self-start">
                <Lock aria-hidden="true" className="size-4" />
                See every unit price
              </Button>
            )}
            <Button href={projectHref} variant="secondary" className="self-start">Back to project</Button>
          </div>
        </header>

        <section aria-label="Price list summary" className="mt-10 grid gap-8 rounded-2xl border border-line bg-surface p-5 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] md:p-7">
          <div className="grid grid-cols-2 gap-x-5 gap-y-7 self-center lg:grid-cols-3">
            <SummaryMetric value={`${summary.available} of ${summary.total}`} label="units available" />
            <SummaryMetric value={priceFrom != null ? `From ${formatPriceShort(priceFrom)}` : "On request"} label="starting price" money />
            <SummaryMetric value={psfFrom != null && psfTo != null ? `${formatPsf(psfFrom).replace(" psf", "")} to ${formatPsf(psfTo)}` : "On request"} label="price per square foot" className="col-span-2 lg:col-span-1" />
            {!unlocked && !soldOut && summary.total > 0 && (
              <div className="col-span-2 lg:col-span-3">
                <Button onClick={() => setUnlockOpen(true)} className="w-full sm:w-auto">
                  <Lock aria-hidden="true" className="size-4" />
                  See every unit price
                </Button>
                <p className="mt-2 text-xs text-ink-muted">Name and mobile, then every price and PSF on this page opens.</p>
              </div>
            )}
          </div>
          {!soldOut && (
            <AvailabilityBars
              embedded
              rows={summary.byType.map((row) => {
                const availableTypePrices = availableUnits
                  .filter((unit) => displayType(unit) === row.type)
                  .map((unit) => unit.price)
                  .filter((price): price is number => price != null && price > 0);
                return {
                  label: row.type,
                  total: row.total,
                  available: row.available,
                  priceFrom: availableTypePrices.length
                    ? Math.min(...availableTypePrices)
                    : null,
                };
              })}
            />
          )}
        </section>

        <section aria-labelledby="floor-plans-title" className="py-14 md:py-20">
          <h2 id="floor-plans-title" className="font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">Floor plans</h2>
          {summary.byPlan.length ? (
            <ScrollRow className="mt-6">
              {summary.byPlan.map((plan) => (
                <div key={`${plan.type}-${plan.plan}`} className="w-[270px] shrink-0 snap-start sm:w-[300px]">
                  <FloorPlanCard plan={plan.plan || "Plan not listed"} type={plan.type} area={{ min: plan.minArea, max: plan.maxArea }} unitsLeft={plan.available} image={floorPlanImage(plan.plan)} onOpen={() => choosePlan(plan.plan)} />
                </div>
              ))}
            </ScrollRow>
          ) : (
            <p className="mt-4 text-sm text-ink-muted">Floor plans have not been released yet.</p>
          )}
        </section>

        <PriceListTable
          units={units}
          locked={!unlocked}
          propertyName={propertyName}
          selectedPlan={selectedPlan}
          onSelectedPlanChange={setSelectedPlan}
          onUnlock={() => setUnlockOpen(true)}
          floorPlanImage={floorPlanImage}
          loadError={loadError}
        />

        <section aria-labelledby="price-list-faq-title" className="py-14 md:py-20">
          <h2 id="price-list-faq-title" className="font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">Questions about the price list</h2>
          <dl className="mt-6 border-t border-line">
            <FaqRow question="Is this the developer's price?">Yes. The developer sets each unit price, which can change as units are released or sold.</FaqRow>
            <FaqRow question="How often is this list updated?">The list is refreshed when the developer provides new availability or pricing.</FaqRow>
            <FaqRow question="Can Winnie get a discount?">Winnie cannot promise a discount. She can confirm the current price and any star-buy units offered by the developer.</FaqRow>
          </dl>
        </section>
      </div>

      <StickyEnquiryBar
        propertyId={propertyId}
        propertyName={propertyName}
        priceFrom={priceFrom}
        status={status.label}
        actionLabel={unlocked ? "Enquire" : "See prices"}
        onAction={() => unlocked ? setEnquiryOpen(true) : setUnlockOpen(true)}
      />
      <UnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} propertyId={propertyId} propertyName={propertyName} onUnlocked={() => setUnlockOpen(false)} />
      <EnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} propertyId={propertyId} title={`Enquire about ${propertyName}`} />
    </main>
  );
}

function SummaryMetric({ value, label, money, className = "" }: { value: string; label: string; money?: boolean; className?: string }) {
  return (
    <div className={className}>
      <p className={`font-display text-xl font-semibold md:text-2xl ${money ? "text-gold-600" : "text-ink"}`}>{value}</p>
      <p className="mt-1 text-sm text-ink-muted">{label}</p>
    </div>
  );
}

function FaqRow({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-2 border-b border-line py-5 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] md:gap-10">
      <dt className="font-semibold text-ink">{question}</dt>
      <dd className="text-sm leading-6 text-ink-muted">{children}</dd>
    </div>
  );
}
